import styled from "@emotion/styled";
import { Box, Button, Typography } from "@avenue-8/sidekick-ui";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { ContactsFilterBar, ContactsList } from "@features/Contacts";
import { PageMoreActions } from "@shared/components/PageMoreActions";
import { ContactsExtractionConsent } from "@features/Contacts/components/ContactsExtractionConsent";
import { ContactsEmptyState } from "@features/Contacts/components/ContactsEmptyState";
import { ContactsProcessingState } from "@features/Contacts/components/ContactsProcessingState";
import { usePageNavbar } from "@shared/components/usePageNavbar";
import type { ContactListItem } from "@features/Contacts/contacts.types";
import { withMainLayout } from "@shared/layouts/withMainLayout";
import { ContactsLayout } from "@features/Contacts/layouts/ContactsLayout";
import { deriveContactName } from "@features/Contacts/contacts-helpers";
import { useGetActionsApps } from "@queries/actions-apps/useGetActionsApps";
import { useGoogleContactsAccess } from "@features/Contacts/hooks/useGoogleContactsAccess";
import { useQueryClient } from "@tanstack/react-query";
import { useUIStore } from "@store/ui.store";
import {
  ContactResponseDto,
  getContactQueryKey,
  listContactsQueryKey,
  useDeleteContact,
  useListContacts,
} from "@shared/generated/crm";
import { CrmSyncIndicator } from "@features/Contacts/components/CrmSyncIndicator";
import { useCrmSyncState } from "@features/Contacts/hooks/useCrmSyncState";
import { RadarMonitoringSummaryBar } from "@features/Contacts/components/RadarMonitoringSummaryBar";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { useListContactMonitoringToggle } from "@features/Contacts/hooks/useContactMonitoringToggle";
import { AddContactMenu } from "@features/Contacts/components/AddContactMenu";
import { useAddContactDialogStore } from "@store/add-contact-dialog.store";

const PAGE_SIZE = 25;

const ToolbarRow = styled.div`
  display: flex;
  align-items: stretch;
  margin-bottom: 1rem;

  ${(p) => p.theme.media.mobileDown} {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

function toContactListItem(contact: ContactResponseDto): ContactListItem {
  const name = deriveContactName(contact);
  const email = contact.email?.[0]?.value ?? "";
  return { id: contact.id, name, email };
}

function ContactsListPageContent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirmDialog = useUIStore((s) => s.confirmDialog);
  const pageRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const connectedAppsQuery = useGetActionsApps();
  const { isConnected: isGoogleConnected, hasAccess: hasGoogleAccess } =
    useGoogleContactsAccess();
  const pageNumber = Number(searchParams.get("page") ?? 1);
  const urlSearch = searchParams.get("search") ?? "";
  const [searchInput, setSearchInput] = useState(urlSearch);
  const openAddContactDialog = useAddContactDialogStore(
    (s) => s.openAddContactDialog,
  );
  const hasRadarModule = useHasModule(sidekickModuleEnum.RADAR);
  const radarOnly = hasRadarModule && searchParams.get("isMonitoring") === "true";

  const updateUrlSearch = useDebouncedCallback((value: string) => {
    setSearchParams(
      (prev: URLSearchParams) => {
        const next = new URLSearchParams(prev);
        if (value) next.set("search", value);
        else next.delete("search");
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  }, 800);

  const handleRadarOnlyChange = (value: boolean) => {
    setSearchParams((prev: URLSearchParams) => {
      const next = new URLSearchParams(prev);
      if (value) next.set("isMonitoring", "true");
      else next.delete("isMonitoring");
      next.set("page", "1");
      return next;
    });
  };

  const needsConsent = !hasGoogleAccess;
  const crmSyncState = useCrmSyncState({ enabled: hasGoogleAccess });
  const isSyncingContacts = crmSyncState.status === "syncing";

  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
    queryKey: activeListContactsQueryKey,
  } = useListContacts(
    {
      search: urlSearch || undefined,
      isMonitoring: radarOnly || undefined,
      sortBy: "latestLeadSignalsAt",
      sortOrder: "desc",
      pageNumber,
      pageSize: PAGE_SIZE,
    },
    {},
    {
      query: {
        refetchInterval: (query) =>
          !urlSearch &&
          !radarOnly &&
          ((query.state.data?.total ?? 0) === 0 || isSyncingContacts)
            ? 15_000
            : false,
        refetchIntervalInBackground: false,
      },
    },
  );

  const showGoogleImportNudge =
    !connectedAppsQuery.isLoading &&
    !connectedAppsQuery.isError &&
    needsConsent;
  const isGoogleSyncActive =
    !connectedAppsQuery.isLoading &&
    !connectedAppsQuery.isError &&
    hasGoogleAccess;
  const showProcessingState =
    isGoogleSyncActive &&
    (isSyncingContacts ||
      (crmSyncState.status === "idle" && crmSyncState.hasErrors));

  const contacts = useMemo(
    () => data?.items?.map(toContactListItem) ?? [],
    [data],
  );

  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const isMonitoredByContactId = useMemo(
    () =>
      (data?.items ?? []).reduce<Record<string, boolean>>((acc, contact) => {
        acc[contact.id] = contact.isMonitoredSince !== null;
        return acc;
      }, {}),
    [data],
  );

  const isMonitoredSinceByContactId = useMemo(
    () =>
      (data?.items ?? []).reduce<Record<string, string | null>>(
        (acc, contact) => {
          acc[contact.id] = contact.isMonitoredSince;
          return acc;
        },
        {},
      ),
    [data],
  );

  const monitoredSinceTimestamps = useMemo(
    () => (data?.items ?? []).map((contact) => contact.isMonitoredSince),
    [data],
  );

  const {
    toggleMonitoring: handleToggleMonitoring,
    pendingContactIds: pendingMonitoringContactIds,
  } = useListContactMonitoringToggle({
    queryKey: activeListContactsQueryKey,
  });

  // Hides a row the instant its toggle flips, ahead of the server refetch.
  const visibleContacts = radarOnly
    ? contacts.filter((c) => isMonitoredByContactId[c.id])
    : contacts;

  useEffect(() => {
    if (isLoading) return;
    if (totalPages <= 0) return;
    if (pageNumber <= totalPages) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(totalPages));
        return next;
      },
      { replace: true },
    );
  }, [isLoading, pageNumber, setSearchParams, totalPages]);

  const {
    mutate: deleteContact,
    isPending: isDeleteContactPending,
    variables: deleteContactVariables,
  } = useDeleteContact({
    mutation: {
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: listContactsQueryKey(),
        });
        queryClient.removeQueries({
          queryKey: getContactQueryKey(variables.contactId),
        });
      },
    },
  });
  const pendingDeleteContactId = isDeleteContactPending
    ? (deleteContactVariables?.contactId ?? null)
    : null;

  const handleDeleteContact = useCallback(
    async (contactId: string, contactName: string) => {
      const confirmed = await confirmDialog({
        title: "Delete contact?",
        content: (
          <Typography>
            This will permanently delete <strong>{contactName}</strong> from
            your CRM.
          </Typography>
        ),
        confirmButtonText: "Delete",
      });
      if (!confirmed) return;
      deleteContact({ contactId });
    },
    [confirmDialog, deleteContact],
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    updateUrlSearch(value);
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev: URLSearchParams) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(page));
      return next;
    });
    pageRef.current?.scrollIntoView({ behavior: "instant" });
  };

  const handleViewBriefing = (contactId: string) => {
    navigate(`/contacts/${contactId}`);
  };

  const header = usePageNavbar({
    title: "Contacts",
    rightElement: (
      <Box display="flex" alignItems="center" gap={2} pr={2}>
        {/* TEMPORARY: restricted to Radar users until "add contact" opens up
            to all CRM-enabled users. */}
        {hasRadarModule && (
          <AddContactMenu
            onAddManually={() => openAddContactDialog("manual")}
            onImportFromCsv={() => openAddContactDialog("csv")}
          />
        )}
        <PageMoreActions />
      </Box>
    ),
  });
  const withLayout = (content: React.ReactNode) => (
    <ContactsLayout header={header}>{content}</ContactsLayout>
  );

  if (isError) {
    return withLayout(
      <Box textAlign={"center"} mt={2}>
        <Typography padding={2}>
          Something went wrong loading your contacts.
        </Typography>
        <Button variant="outlined" size="small" onClick={() => refetch()}>
          Refresh
        </Button>
      </Box>,
    );
  }

  if (!isLoading && totalCount === 0 && !urlSearch && !radarOnly) {
    const isEmptyStateLoading =
      connectedAppsQuery.isLoading || crmSyncState.status === "loading";
    return withLayout(
      isEmptyStateLoading ? null : connectedAppsQuery.isError ? (
        <Box textAlign="center" mt={2}>
          <Typography padding={2}>
            Something went wrong loading your integrations.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => connectedAppsQuery.refetch()}
          >
            Refresh
          </Button>
        </Box>
      ) : showProcessingState ? (
        <ContactsProcessingState />
      ) : (
        <ContactsEmptyState
          onAddContact={() => openAddContactDialog("manual")}
          onImportFromCsv={() => openAddContactDialog("csv")}
          isGoogleConnected={isGoogleConnected}
          hasGoogleAccess={hasGoogleAccess}
          hasRadarModule={hasRadarModule}
        />
      ),
    );
  }

  return withLayout(
    <Box ref={pageRef}>
      {showGoogleImportNudge && (
        <ContactsExtractionConsent
          size="banner"
          isConnected={isGoogleConnected}
        />
      )}
      <ContactsFilterBar
        filterValue={searchInput}
        onFilterValueChange={handleSearchChange}
        isSearching={!!searchInput && (searchInput !== urlSearch || isFetching)}
      />
      <ToolbarRow>
        <CrmSyncIndicator syncingLabel="Syncing contacts..." />
        {hasRadarModule && (
          <RadarMonitoringSummaryBar
            radarOnly={radarOnly}
            onRadarOnlyChange={handleRadarOnlyChange}
            monitoredSinceTimestamps={monitoredSinceTimestamps}
          />
        )}
      </ToolbarRow>
      <ContactsList
        contacts={visibleContacts}
        isLoading={isLoading}
        onViewBriefing={handleViewBriefing}
        onDeleteContact={handleDeleteContact}
        pendingDeleteContactId={pendingDeleteContactId}
        page={pageNumber}
        totalPages={totalPages}
        onChangePage={handlePageChange}
        isMonitoredByContactId={isMonitoredByContactId}
        isMonitoredSinceByContactId={isMonitoredSinceByContactId}
        pendingMonitoringContactIds={pendingMonitoringContactIds}
        onToggleMonitoring={handleToggleMonitoring}
      />
    </Box>,
  );
}

export const ContactsListPage = withMainLayout(ContactsListPageContent);
